import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Check, Sparkles } from "lucide-react";

export default function Pricing() {
  const [packages, setPackages] = useState([]);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState("");
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/plans").then((r) => setPackages(r.data)).catch((e) => setErr(formatApiError(e)));
  }, []);

  const startCheckout = async (pkgKey) => {
    if (!user || typeof user !== "object") {
      navigate("/login");
      return;
    }
    setBusy(pkgKey);
    try {
      const origin = window.location.origin;
      const { data } = await api.post("/payments/checkout", { package_id: pkgKey, origin_url: origin });
      if (data?.url) {
        window.location.href = data.url;
      } else {
        setErr("Checkout URL missing.");
      }
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setBusy("");
    }
  };

  const featureFallback = {
    monthly: [
      "Lås op for fulde profil-detaljer",
      "Tydelige fotos (ingen slør)",
      "Privat, filtreret samtale",
      "Prioriteret kuratering — 30 dage",
    ],
    full_package: [
      "Alt i Månedlig",
      "Seks måneders fuld adgang",
      "Prioriteret placering i forslag",
      "Adgang til premium-introduktioner",
    ],
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="pricing-page">
      <div className="text-center max-w-2xl mx-auto">
        <div className="eyebrow">Medlemskab</div>
        <h1 className="mt-3 font-serif text-4xl sm:text-5xl text-olive-700">Seriøse medlemmer. Seriøse resultater.</h1>
        <p className="mt-4 text-brown-600">
          Medlemskab låser op for samtaler, tydelige fotos og fuld biodata. De fleste medlemmer finder en seriøs introduktion inden for de første 30 dage.
        </p>
      </div>

      {err && <div className="mt-6 text-destructive text-sm text-center">{err}</div>}
      {user && typeof user === "object" && user.subscription?.active && (
        <div className="mt-8 card-soft p-4 bg-olive-100 border-olive-500/30 text-olive-700 text-sm text-center" data-testid="already-subscribed-banner">
          Du har allerede et aktivt medlemskab indtil <strong>{new Date(user.subscription.expires_at).toLocaleDateString("da-DK")}</strong>.
        </div>
      )}

      <div className="mt-12 grid md:grid-cols-2 gap-6">
        {packages.map((p) => {
          const isFeatured = !!p.highlight;
          const featureList = (p.features && p.features.length > 0) ? p.features : (featureFallback[p.key] || []);
          return (
            <div key={p.id} className={`card-soft p-8 relative ${isFeatured ? "border-gold-500/60 shadow-medium" : ""}`} data-testid={`plan-${p.key}`}>
              {isFeatured && (
                <div className="absolute -top-3 left-6 bg-gold-500 text-white text-xs px-3 py-1 rounded-full flex items-center gap-1">
                  <Sparkles className="h-3 w-3" /> Anbefalet
                </div>
              )}
              <div className="eyebrow">{p.duration_days} dage</div>
              <h3 className="mt-2 font-serif text-3xl text-olive-700">{p.name}</h3>
              <div className="mt-3 flex items-baseline gap-1">
                <span className="font-serif text-5xl text-olive-700">${p.price}</span>
                <span className="text-brown-500 uppercase text-xs">{p.currency}</span>
              </div>
              <p className="mt-4 text-brown-600 text-sm">{p.description}</p>
              <ul className="mt-6 space-y-2 text-sm text-brown-600">
                {featureList.map((f) => (
                  <li key={f} className="flex items-start gap-2"><Check className="h-4 w-4 text-olive-500 mt-0.5" /> {f}</li>
                ))}
              </ul>
              <button
                className={`mt-8 w-full ${isFeatured ? "btn-primary" : "btn-secondary"}`}
                onClick={() => startCheckout(p.key)}
                disabled={busy === p.key}
                data-testid={`subscribe-${p.key}`}
              >
                {busy === p.key ? "Viderestiller…" : `Vælg ${p.name}`}
              </button>
              <div className="mt-4 text-xs text-brown-500 text-center">Apple Pay og Google Pay understøttes via Stripe.</div>
            </div>
          );
        })}
      </div>

      <div className="mt-12 text-center text-sm text-brown-500">
        <Link to="/matches" className="hover:text-olive-600">← Tilbage til forslag</Link>
      </div>
    </div>
  );
}
